import React, { useState, useEffect, useRef } from "react";
import "./login.css";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FormData from "form-data";
import { signIn, forgotPasswordSendOtp, forgotPasswordReset } from "../../redux/actions/action-creator";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import LoginInput from "../Input/LoginInput";
import useClickOutside from "../Custom_Hooks/useClickOutsSide";
import Indicator from "../Indicator/Indicator";

const initialState = {
  customercode: "",
  userName: "",
  password: "",
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customerCode } = useParams();

  const { userData = null } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(initialState);
  const [visible, setVisible] = useState(false);
  const [focus, setFocus] = useState(false);
  const [nameInput, setNameInput] = useState(null);

  const [forgotMode, setForgotMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [forgotForm, setForgotForm] = useState({
    customer_code: "",
    username: "",
    email: "",
    otp: "",
    new_password: "",
  });
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotChange = (e) => {
    const { name, value } = e.target;
    setForgotForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenForgotMode = () => {
    setForgotMode(true);
    setForgotForm((prev) => ({
      ...prev,
      customer_code: state.customercode || customerCode || "",
      username: state.userName || "",
    }));
  };

  const handleSendOtp = () => {
    const { customer_code, username, email } = forgotForm;
    if (!customer_code || !username || !email) {
      toast.warn("Please fill Customer Code, Username, and Email!", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }
    setForgotLoading(true);
    const fd = new FormData();
    fd.append("customer_code", customer_code);
    fd.append("username", username);
    fd.append("email", email);

    dispatch(forgotPasswordSendOtp(fd))
      .then((res) => {
        setForgotLoading(false);
        if (res?.status) {
          toast.success(res.message || "OTP sent successfully!", {
            position: toast.POSITION.TOP_CENTER,
          });
          setOtpSent(true);
        } else {
          toast.warn(res?.message || "Failed to send OTP", {
            position: toast.POSITION.TOP_CENTER,
          });
        }
      })
      .catch((err) => {
        setForgotLoading(false);
        toast.error(err?.message || "Error occurred while sending OTP", {
          position: toast.POSITION.TOP_CENTER,
        });
      });
  };

  const handleResetPassword = () => {
    const { customer_code, username, email, otp, new_password } = forgotForm;
    if (!otp || !new_password) {
      toast.warn("Please enter OTP and New Password!", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }
    setForgotLoading(true);
    const fd = new FormData();
    fd.append("customer_code", customer_code);
    fd.append("username", username);
    fd.append("email", email);
    fd.append("otp", otp);
    fd.append("new_password", new_password);

    dispatch(forgotPasswordReset(fd))
      .then((res) => {
        setForgotLoading(false);
        if (res?.status) {
          toast.success(res.message || "Password reset successfully!", {
            position: toast.POSITION.TOP_CENTER,
          });
          setForgotMode(false);
          setOtpSent(false);
          setForgotForm({
            customer_code: "",
            username: "",
            email: "",
            otp: "",
            new_password: "",
          });
        } else {
          toast.warn(res?.message || "Failed to reset password", {
            position: toast.POSITION.TOP_CENTER,
          });
        }
      })
      .catch((err) => {
        setForgotLoading(false);
        toast.error(err?.message || "Error occurred while resetting password", {
          position: toast.POSITION.TOP_CENTER,
        });
      });
  };

  const customercode_input_ref = useRef(null);
  const userName_input_ref = useRef(null);
  const password_input_ref = useRef(null);

  const forgotCustomerCodeRef = useRef(null);
  const forgotUsernameRef = useRef(null);
  const forgotEmailRef = useRef(null);
  const forgotOtpRef = useRef(null);
  const forgotNewPasswordRef = useRef(null);

  const handleForgotKeyDown = (e) => {
    const { name, value } = e.target;
    if (e.key === "Enter") {
      e.preventDefault();
      if (name === "customer_code") {
        if (value.trim() !== "") {
          forgotUsernameRef?.current?.focus();
        } else {
          toast.info("Please fill Customer Code first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else if (name === "username") {
        if (value.trim() !== "") {
          forgotEmailRef?.current?.focus();
        } else {
          toast.info("Please fill Username first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else if (name === "email") {
        if (value.trim() !== "") {
          handleSendOtp();
        } else {
          toast.info("Please fill Email first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else if (name === "otp") {
        if (value.trim() !== "") {
          forgotNewPasswordRef?.current?.focus();
        } else {
          toast.info("Please enter OTP first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else if (name === "new_password") {
        if (value.trim() !== "") {
          handleResetPassword();
        } else {
          toast.info("Please enter New Password first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (forgotMode) {
      setTimeout(() => {
        if (!forgotForm.customer_code) {
          forgotCustomerCodeRef?.current?.focus();
        } else if (!forgotForm.username) {
          forgotUsernameRef?.current?.focus();
        } else {
          forgotEmailRef?.current?.focus();
        }
      }, 100);
    }
  }, [forgotMode]);

  useEffect(() => {
    if (otpSent) {
      setTimeout(() => {
        forgotOtpRef?.current?.focus();
      }, 100);
    }
  }, [otpSent]);

  const { customercode, userName, password } = state;

  useEffect(() => {
    if (customerCode) {
      setState((prev) => ({ ...prev, customercode: customerCode }));
    }
  }, [customerCode]);

  useEffect(() => {
    if (customerCode && !loading) {
      userName_input_ref?.current?.focus();
    }
  }, [customerCode, loading]);

  const handleClickOutside = (event) => {
    if (nameInput.current) {
      event.preventDefault();
      nameInput.current.focus();
    }
  };

  const handleClickInside = (event) => {
    let clickedElement = event.target;
    if (
      clickedElement?.tagName &&
      (clickedElement?.tagName.toLowerCase() === "input" ||
        clickedElement?.tagName.toLowerCase() === "textarea")
    ) {
      const fieldName = clickedElement.name;

      // Check if the click is allowed based on conditional logic
      if (!isFocusAllowed(fieldName)) {
        event.preventDefault();
        redirectToAllowedField(fieldName);
        return;
      }

      clickedElement.focus();
    }
  };

  useClickOutside(nameInput, handleClickOutside, handleClickInside);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  const handleKeyDown = (e) => {
    const { name, value } = e.target;
    if (e.key === "Enter") {
      e.preventDefault();
      if (name === "customercode") {
        // Only move to username if customer code is not empty
        if (value.trim() !== "") {
          userName_input_ref?.current?.focus();
        } else {
          toast.info("Please fill Customer Code first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else if (name === "userName") {
        // Only move to password if both customer code and username are not empty
        if (customercode.trim() !== "" && value.trim() !== "") {
          password_input_ref?.current?.focus();
        } else if (customercode.trim() === "") {
          customercode_input_ref?.current?.focus();
          toast.info("Please fill Customer Code first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        } else {
          toast.info("Please fill Username first!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else if (name === "password") {
        // Only submit if all fields are filled
        if (
          customercode.trim() !== "" &&
          userName.trim() !== "" &&
          value.trim() !== ""
        ) {
          handleSubmit();
        } else {
          toast.info("Please fill all fields!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      }
    }else if(e.shiftKey && e.key === "Tab"){
      e.preventDefault();
      if(name === "password"){
        userName_input_ref?.current?.focus();
      }else if(name === "userName"){
        customercode_input_ref?.current?.focus();
      }
    }
  };
  const handleFocus = (e) => {
    const { name } = e.target;

    // Check if the focus is allowed based on conditional logic
    if (!isFocusAllowed(name)) {
      e.preventDefault();
      redirectToAllowedField(name);
      return;
    }

    const inputRefs = {
      customercode: customercode_input_ref,
      userName: userName_input_ref,
      password: password_input_ref,
    };
    setNameInput(inputRefs[name]);
    if (name === "customercode") {
      customercode_input_ref?.current?.setSelectionRange(
        0,
        customercode_input_ref?.current?.value?.length
      );
    } else if (name === "userName") {
      userName_input_ref?.current?.setSelectionRange(
        0,
        userName_input_ref?.current?.value?.length
      );
    } else if (name === "password") {
      password_input_ref?.current?.setSelectionRange(
        0,
        password_input_ref?.current?.value?.length
      );
      setFocus(true);
    }
  };

  // Function to check if focus is allowed on a specific field
  const isFocusAllowed = (fieldName) => {
    switch (fieldName) {
      case "customercode":
        // Customer code can always be focused
        return true;

      case "userName":
        // Username can be focused if:
        // 1. Customer code is not empty, OR
        // 2. Customer code is empty (to allow going back)
        return true; // We'll handle the logic in redirectToAllowedField

      case "password":
        // Password can be focused if:
        // 1. Customer code is not empty AND username is not empty, OR
        // 2. Username is empty OR customer code is empty (to allow going back)
        return customercode !== "" && userName !== "";

      default:
        return true;
    }
  };

  // Function to redirect focus to the appropriate field
  const redirectToAllowedField = (attemptedField) => {
    switch (attemptedField) {
      case "userName":
        // If customer code is empty, focus on customer code
        if (customercode === "") {
          setTimeout(() => {
            customercode_input_ref?.current?.focus();
            toast.info("Please fill Customer Code first!", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000,
            });
          }, 0);
        }
        // If customer code is not empty, allow focus on username
        break;

      case "password":
        // If customer code is empty, focus on customer code
        if (customercode === "") {
          setTimeout(() => {
            customercode_input_ref?.current?.focus();
            toast.info("Please fill Customer Code first!", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000,
            });
          }, 0);
        }
        // If username is empty but customer code is filled, focus on username
        else if (userName === "") {
          setTimeout(() => {
            userName_input_ref?.current?.focus();
            toast.info("Please fill Username first!", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000,
            });
          }, 0);
        }
        break;

      default:
        break;
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (name === "customercode") {
      if (!value) {
          customercode_input_ref?.current?.focus();
      }
    }

    if (name === "userName") {
      if (!value) {
        customercode_input_ref?.current?.focus();
      }
    }

    if (name === "password") {
      setFocus(false);
      if (!value) {
      }
    }
  };

  const handleSubmit = (e1) => {
    if (state.customercode !== "") {
      if (state.userName !== "") {
        if (state.password !== "") {
          setLoading(true);
          let data = new FormData();
          data.append("user_name", userName);
          data.append("password", password);
          data.append("customer_code", customercode);
          dispatch(signIn(data))
            .then((response) => {
              if (response.status) {
                setState({ customercode: "", userName: "", password: "" });
                toast.success(response?.message || "Login Success!", {
                  position: toast.POSITION.TOP_CENTER,
                });
                localStorage.setItem("authToken", userData?.id);
                setTimeout(() => {
                  setLoading(false);
                  navigate('/');
                }, 1000);
              } else {
                toast.warn(response?.message || "Login Fail !", {
                  position: toast.POSITION.TOP_CENTER,
                });
                setLoading(false);
              }
            })
            .catch((error) => {
              console.log(error);
              toast.error(error?.message || "Login Failed!", {
                position: toast.POSITION.TOP_CENTER,
              });
              setLoading(false);
            });
        } else {
          toast.warn("Fill Password ...!", {
            position: toast.POSITION.TOP_CENTER,
          });
        }
      } else {
        toast.warn("Fill Username ...!", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } else {
      toast.warn("Fill Customer Code ...!", {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="container-fluid" id="login-container">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
      {loading ? (
 <Indicator/>
      ) : (
        <div className="row justify-content-center align-items-center" id="row">
          <div className="col-lg-4 p-5">
            <div className="row" id="card">
              <div className="col">
                <form method="post" className="mt-3" autoComplete="off">
                  {!forgotMode ? (
                    <>
                      <LoginInput
                        lable="Customer Code"
                        type="text"
                        handleChange={handleChange}
                        handleFocus={handleFocus}
                        handleBlur={handleBlur}
                        handleKeyDown={handleKeyDown}
                        value={customercode}
                        name="customercode"
                        ref={customercode_input_ref}
                        disabled={!!customerCode}
                        autoComplete="one-time-code"
                      />
                      <LoginInput
                        lable="Username"
                        type="text"
                        handleChange={handleChange}
                        handleFocus={handleFocus}
                        handleBlur={handleBlur}
                        handleKeyDown={handleKeyDown}
                        value={userName}
                        name="userName"
                        ref={userName_input_ref}
                        disabled={customercode.trim() === ""}
                        autoComplete="one-time-code"
                      />
                      <LoginInput
                        lable="Password"
                        type={visible ? "text" : "password"}
                        handleChange={handleChange}
                        handleFocus={handleFocus}
                        handleBlur={handleBlur}
                        handleKeyDown={handleKeyDown}
                        value={password}
                        name="password"
                        ref={password_input_ref}
                        visible={visible}
                        setVisible={setVisible}
                        focus={focus}
                        disabled={
                          customercode.trim() === "" || userName.trim() === ""
                        }
                        autoComplete="one-time-code"
                      />

                      <ul className="nav">
                        <li className="nav-item">
                          <span 
                            className="nav-link p-0 active forgot_text"
                            onClick={handleOpenForgotMode}
                            style={{ cursor: "pointer" }}
                          >
                            Forgot password?
                          </span>
                        </li>
                      </ul>
                      <div className="border"></div>
                      <div className="input-group input-group-lg mb-3 mt-3">
                        <button
                          type="button"
                          style={{ width: "100%", fontSize: 15 }}
                          className="btn btn-success"
                          onClick={() => {
                            return handleSubmit();
                          }}
                        >
                          <strong>Login</strong>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h5 className="text-center mb-3 text-success">Forgot Password</h5>
                      
                       {!otpSent ? (
                        <>
                          <LoginInput
                            lable="Customer Code"
                            type="text"
                            handleChange={handleForgotChange}
                            handleKeyDown={handleForgotKeyDown}
                            value={forgotForm.customer_code}
                            name="customer_code"
                            ref={forgotCustomerCodeRef}
                            autoComplete="one-time-code"
                          />
                          <LoginInput
                            lable="Username"
                            type="text"
                            handleChange={handleForgotChange}
                            handleKeyDown={handleForgotKeyDown}
                            value={forgotForm.username}
                            name="username"
                            ref={forgotUsernameRef}
                            autoComplete="one-time-code"
                          />
                          <LoginInput
                            lable="Email Address"
                            type="email"
                            handleChange={handleForgotChange}
                            handleKeyDown={handleForgotKeyDown}
                            value={forgotForm.email}
                            name="email"
                            ref={forgotEmailRef}
                            autoComplete="one-time-code"
                          />
                          
                          <div className="input-group input-group-lg mb-2 mt-3">
                            <button
                              type="button"
                              style={{ width: "100%", fontSize: 15 }}
                              className="btn btn-success"
                              onClick={handleSendOtp}
                              disabled={forgotLoading}
                            >
                              <strong>{forgotLoading ? "Sending OTP..." : "Send OTP"}</strong>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-muted mb-2 text-center" style={{ fontSize: '12px' }}>
                            OTP has been sent to <b>{forgotForm.email}</b>.
                          </div>
                          <LoginInput
                            lable="Enter 6-Digit OTP"
                            type="text"
                            handleChange={handleForgotChange}
                            handleKeyDown={handleForgotKeyDown}
                            value={forgotForm.otp}
                            name="otp"
                            maxLength={6}
                            ref={forgotOtpRef}
                            autoComplete="one-time-code"
                          />
                          <LoginInput
                            lable="New Password"
                            type="password"
                            handleChange={handleForgotChange}
                            handleKeyDown={handleForgotKeyDown}
                            value={forgotForm.new_password}
                            name="new_password"
                            ref={forgotNewPasswordRef}
                            autoComplete="one-time-code"
                          />
                          
                          <div className="input-group input-group-lg mb-2 mt-3">
                            <button
                              type="button"
                              style={{ width: "100%", fontSize: 15 }}
                              className="btn btn-success"
                              onClick={handleResetPassword}
                              disabled={forgotLoading}
                            >
                              <strong>{forgotLoading ? "Resetting..." : "Reset Password"}</strong>
                            </button>
                          </div>
                          
                          <div className="text-center mb-2">
                            <span 
                              onClick={handleSendOtp} 
                              style={{ cursor: "pointer", fontSize: "12px", color: "#198754", fontWeight: "bold" }}
                            >
                              Resend OTP
                            </span>
                          </div>
                        </>
                      )}
                      
                      <div className="text-center mt-3">
                        <span 
                          onClick={() => { setForgotMode(false); setOtpSent(false); }} 
                          style={{ cursor: "pointer", fontSize: "13px", textDecoration: "underline", color: "#198754", fontWeight: "bold" }}
                        >
                          Back to Login
                        </span>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Login);
